"use client";

import Image from "next/image";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useState } from "react";
import { TeamStats } from "@/lib/teamData";
import { Button } from "@/components/ui/button";
import { TEAM_ABBREVIATIONS } from "@/lib/constants";

type SortField = "position" | "actualPosition" | "points" | "goalDifference" | "wins" | "draws" | "losses" | "played";
type SortDirection = "asc" | "desc";

export function SortableTable({ teams }: { teams: TeamStats[] }) {
  const [sortField, setSortField] = useState<SortField>("actualPosition");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const resetSort = () => {
    setSortField("actualPosition");
    setSortDirection("asc");
  };

  const sortedTeams = [...teams].sort((a, b) => {
    const multiplier = sortDirection === "asc" ? 1 : -1;
    if (sortField === "position" || sortField === "actualPosition") {
      return (a[sortField] - b[sortField]) * multiplier;
    }
    return (b[sortField] - a[sortField]) * multiplier;
  });
  
  return (
    <div className="w-full">
      <div className="flex justify-end mb-2">
        <Button 
          variant="outline" 
          onClick={resetSort}
          className="text-sm h-8 px-3"
        >
          Reset
        </Button>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead 
                  className="cursor-pointer hover:bg-muted whitespace-nowrap px-2 py-2"
                  onClick={() => handleSort("position")}
                >
                  Mini {sortField === "position" && (sortDirection === "asc" ? "↑" : "↓")}
                </TableHead>
                <TableHead className="whitespace-nowrap px-2 py-2">Team</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted whitespace-nowrap px-2 py-2"
                  onClick={() => handleSort("actualPosition")}
                >
                  PL {sortField === "actualPosition" && (sortDirection === "asc" ? "↑" : "↓")}
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted whitespace-nowrap px-2 py-2"
                  onClick={() => handleSort("played")}
                >
                  Played {sortField === "played" && (sortDirection === "asc" ? "↑" : "↓")}
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted whitespace-nowrap px-2 py-2"
                  onClick={() => handleSort("wins")}
                >
                  W {sortField === "wins" && (sortDirection === "asc" ? "↑" : "↓")}
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted whitespace-nowrap px-2 py-2"
                  onClick={() => handleSort("draws")}
                >
                  D {sortField === "draws" && (sortDirection === "asc" ? "↑" : "↓")}
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted whitespace-nowrap px-2 py-2"
                  onClick={() => handleSort("losses")}
                >
                  L {sortField === "losses" && (sortDirection === "asc" ? "↑" : "↓")}
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted whitespace-nowrap px-2 py-2"
                  onClick={() => handleSort("goalDifference")}
                >
                  GD {sortField === "goalDifference" && (sortDirection === "asc" ? "↑" : "↓")}
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted whitespace-nowrap px-2 py-2"
                  onClick={() => handleSort("points")}
                >
                  Pts {sortField === "points" && (sortDirection === "asc" ? "↑" : "↓")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTeams.map((team, index) => (
                <TableRow 
                  key={team.team}
                  className={index === 1 ? "border-b-2 border-muted" : ""}
                >
                  <TableCell className="font-bold whitespace-nowrap px-2 py-2">{team.position}</TableCell>
                  <TableCell className="flex items-center gap-1 whitespace-nowrap px-2 py-2">
                    <Image
                      src={team.logo}
                      alt={`${team.team} logo`}
                      width={24}
                      height={24}
                      className="object-contain"
                    />
                    <span>{TEAM_ABBREVIATIONS[team.team]}</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap px-2 py-2">{team.actualPosition}</TableCell>
                  <TableCell className="whitespace-nowrap px-2 py-2">{team.played}</TableCell>
                  <TableCell className="whitespace-nowrap px-2 py-2">{team.wins}</TableCell>
                  <TableCell className="whitespace-nowrap px-2 py-2">{team.draws}</TableCell>
                  <TableCell className="whitespace-nowrap px-2 py-2">{team.losses}</TableCell>
                  <TableCell className="whitespace-nowrap px-2 py-2">{team.goalDifference}</TableCell>
                  <TableCell className="font-bold whitespace-nowrap px-2 py-2">{team.points}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}